import { UseFormReturn } from 'react-hook-form';
import { ClipLoader } from 'react-spinners';
import { MaintenanceRecordWithDisplayName } from '../../../../../../../types/apiTypes';
import { formatDate } from '../../../../../../../utils/DateUtils';
import { MaintenanceAsset, MaintenanceFormData } from './types';

/**
 * The reminder-specific field set: optional asset picker (when no asset is
 * pre-selected), type, "link to existing record", and the remind-at sub-form.
 * Ported field-for-field from Add/EditMaintenanceReminderModal. The mileage
 * option is gated on the asset having an ELD (the desktop behavior, adopted as
 * canonical — the legacy tablet branch had a buggy precedence check and a
 * stray number-formatter that the other breakpoints lacked).
 */
export function ReminderFields({
  form,
  selectedAsset,
  maintenanceRecords,
  assetList,
  fetchedRecords,
  loadingRecords,
}: {
  form: UseFormReturn<MaintenanceFormData>;
  selectedAsset?: MaintenanceAsset;
  maintenanceRecords: MaintenanceRecordWithDisplayName[];
  assetList?: MaintenanceAsset[];
  fetchedRecords: MaintenanceRecordWithDisplayName[];
  loadingRecords: boolean;
}) {
  const {
    register,
    watch,
    formState: { errors },
  } = form;
  const reminderType = watch('reminderType');

  const recordOptionLabel = (record: MaintenanceRecordWithDisplayName) =>
    record.asset_display_name +
    ' ' +
    record?.maintenance_type +
    ' ' +
    formatDate(new Date(record?.date_of_maintenance ?? new Date()), true);

  return (
    <>
      {!selectedAsset && (
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Choose Existing Asset
          </label>
          <select
            {...register('pickedAssetFromList', {})}
            className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
          >
            <option key="existing-asset" value="" disabled>
              Existing Asset
            </option>
            {assetList?.map((asset) => (
              <option
                key={asset?.truck_id || asset?.trailer_id}
                value={
                  (asset?.truck_id || asset?.trailer_id) +
                  '#' +
                  asset?.asset_type
                }
              >
                {(asset?.asset_type
                  ? asset.asset_type[0].toUpperCase() +
                    asset.asset_type.slice(1)
                  : '') +
                  ' ' +
                  (asset?.truck_display_name ?? asset?.trailer_display_name) +
                  ' ' +
                  asset?.vehicle_id_number}
              </option>
            ))}
          </select>
        </div>
      )}
      <div>
        <label className="mb-2 block text-sm font-medium text-gray-700">
          Type
        </label>
        <select
          {...register('maintenanceType', { required: 'Type is required' })}
          className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
        >
          <option value="" disabled>
            Choose a Type
          </option>
          <option value="repairs">Repairs</option>
          <option value="tires">Tires</option>
          <option value="preventative">Preventative</option>
        </select>
        {errors.maintenanceType && (
          <p className="mt-1 text-sm text-red-500">
            {errors.maintenanceType.message}
          </p>
        )}
      </div>
      {!selectedAsset &&
      (!maintenanceRecords || maintenanceRecords?.length === 0) ? (
        <>
          {!loadingRecords ? (
            fetchedRecords && fetchedRecords?.length > 0 ? (
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Link to Existing Maintenance Record
                </label>
                <select
                  {...register('recordId', {})}
                  className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
                >
                  <option value="" disabled>
                    Existing Record
                  </option>
                  {fetchedRecords.map((record) => (
                    <option key={record?.record_id} value={record?.record_id}>
                      {recordOptionLabel(record)}
                    </option>
                  ))}
                </select>
                {errors.recordId && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.recordId.message}
                  </p>
                )}
              </div>
            ) : (
              <h2> No Previous Maintenance Records Found.</h2>
            )
          ) : (
            <div className="flex w-full flex-row items-start justify-start space-x-2 align-middle">
              <ClipLoader
                color="#1D213B"
                loading={loadingRecords}
                size={20}
                aria-label="Loading Spinner"
                data-testid="loader"
                className="w-full justify-center"
              />
              <h2 className="text-[16px] text-primary">
                Loading maintenance records
              </h2>
            </div>
          )}
        </>
      ) : (
        <div>
          <label className="mb-2 block text-sm font-medium text-gray-700">
            Link to Existing Maintenance Record
          </label>
          <select
            {...register('recordId', {})}
            className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
          >
            <option value="" disabled>
              Existing Record
            </option>
            {maintenanceRecords.map((record) => (
              <option key={record?.record_id} value={record?.record_id}>
                {recordOptionLabel(record)}
              </option>
            ))}
          </select>
          {errors.recordId && (
            <p className="mt-1 text-sm text-red-500">
              {errors.recordId.message}
            </p>
          )}
        </div>
      )}

      <div className="flex flex-col gap-6">
        <div>
          <p className="text-sm font-medium text-gray-700">Remind at</p>
          <div className="flex space-x-4">
            <label className="flex items-center">
              <input
                {...register('reminderType', {
                  required: 'Please choose when to remind you',
                })}
                type="radio"
                value="date"
                className="mr-2"
              />
              Date
            </label>
            <label className="flex items-center">
              <input
                {...register('reminderType', {
                  required: 'Please choose when to remind you',
                })}
                type="radio"
                value="days"
                className="mr-2"
              />
              After # of days
            </label>
            {selectedAsset?.eld_id && selectedAsset?.eld_id?.length > 0 && (
              <label className="flex items-center">
                <input
                  {...register('reminderType', {
                    required: 'Please choose when to remind you',
                  })}
                  type="radio"
                  value="mileage"
                  className="mr-2"
                />
                After # of miles
              </label>
            )}
          </div>
          {errors.reminderType && (
            <p className="mt-1 text-sm text-red-500">
              {errors.reminderType.message}
            </p>
          )}
        </div>
        {reminderType === 'date' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Date
            </label>
            <input
              type="date"
              {...register('reminderValue', { required: 'Date is required' })}
              className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
            />
          </div>
        )}
        {reminderType === 'days' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              After # of Days
            </label>
            <input
              type="number"
              {...register('reminderValue', {
                required: 'Number of days is required',
              })}
              placeholder="Enter number of days"
              className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
            />
          </div>
        )}
        {reminderType === 'mileage' && (
          <div>
            <label className="block text-sm font-medium text-gray-700">
              After # of Miles
            </label>
            <input
              type="number"
              {...register('reminderValue', {
                required: 'Number of miles is required',
              })}
              placeholder="Enter number of miles"
              className="w-full rounded-md border border-gray-300 p-2 focus:outline-none"
            />
          </div>
        )}
      </div>
    </>
  );
}

export default ReminderFields;
